import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from '../context/AuthContext';
import { apiPost } from '../services/api';

const CookieConsentContext = createContext(null);

const STORAGE_KEY = 'rwm_cookie_consent';
const CONSENT_VERSION = '1';

function readStoredConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CONSENT_VERSION) return null;
    if (typeof parsed.analytics !== 'boolean') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredConsent(analytics) {
  const payload = {
    analytics,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

export function CookieConsentProvider({ children }) {
  const { token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [decided, setDecided] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const stored = readStoredConsent();
    if (stored) {
      setAnalytics(stored.analytics);
      setDecided(true);
    }
    setReady(true);
  }, []);

  const saveToDb = useCallback(
    async (prefs) => {
      if (!token) return;
      try {
        await apiPost('/api/v1/consent/cookies', { analytics: prefs.analytics }, token);
      } catch {
        // Non-blocking — localStorage is the primary store for guests
      }
    },
    [token]
  );

  const persistConsent = useCallback(
    async (nextAnalytics) => {
      writeStoredConsent(nextAnalytics);
      setAnalytics(nextAnalytics);
      setDecided(true);
      await saveToDb({ analytics: nextAnalytics });
    },
    [saveToDb]
  );

  const acceptAll = useCallback(async () => {
    await persistConsent(true);
  }, [persistConsent]);

  const rejectAll = useCallback(async () => {
    await persistConsent(false);
  }, [persistConsent]);

  const updateConsent = useCallback(
    async (prefs) => {
      if (typeof prefs.analytics !== 'boolean') return;
      await persistConsent(prefs.analytics);
    },
    [persistConsent]
  );

  const value = useMemo(
    () => ({
      analytics,
      decided,
      ready,
      acceptAll,
      rejectAll,
      updateConsent,
    }),
    [analytics, decided, ready, acceptAll, rejectAll, updateConsent]
  );

  return (
    <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return ctx;
}
